import { EmptyState, ErrorAlert, Spinner, TenantProvider, createIdentityCacheGuard } from '@lumlab/platform-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { useAccess } from '@lumlab/platform-admin/web';
import { authClient } from './auth.ts';

/**
 * Lets signed-in users through. Without an active organization, activates the first one they belong to: the API
 * resolves the organization from the session, never from the URL or the request body.
 */
export function RequireSession({
    children,
    organizationRequired = true,
}: {
    children: ReactNode;
    organizationRequired?: boolean;
}) {
    const { t } = useTranslation();
    const session = authClient.useSession();
    const access = useAccess(session.data?.user.id);
    const [noOrganization, setNoOrganization] = useState(false);
    const activeOrganizationId = session.data?.session.activeOrganizationId;
    const queryClient = useQueryClient();
    const guard = useRef(createIdentityCacheGuard(queryClient));
    const identity = session.data ? `${session.data.user.id}:${activeOrganizationId ?? 'global'}` : null;

    useEffect(() => {
        if (!session.isPending) {
            guard.current(identity);
        }
    }, [identity, session.isPending]);

    useEffect(() => {
        if (
            !session.data ||
            activeOrganizationId ||
            !organizationRequired ||
            !access.data ||
            access.data.administrator
        ) {
            return;
        }
        void (async () => {
            const organizations = await authClient.organization.list();
            const first = organizations.data?.[0];
            if (!first) {
                setNoOrganization(true);
                return;
            }
            await authClient.organization.setActive({ organizationId: first.id });
            await session.refetch();
        })();
    }, [session, activeOrganizationId, organizationRequired, access.data]);

    if (session.isPending) {
        return (
            <div className="flex items-center justify-center min-h-dvh">
                <Spinner label={t('platform.shell.loading')} />
            </div>
        );
    }
    if (!session.data) {
        return <Navigate to="/sign-in" replace />;
    }
    if (!organizationRequired) return children;
    if (access.error) return <ErrorAlert error={access.error} />;
    if (access.data?.administrator && !activeOrganizationId) return <Navigate to="/admin" replace />;
    if (noOrganization) {
        return (
            <div className="flex items-center justify-center p-4 min-h-dvh">
                <EmptyState
                    title={t('platform.shell.noOrganization')}
                    description={t('platform.shell.noOrganizationHelp')}
                />
            </div>
        );
    }
    if (!activeOrganizationId) {
        return (
            <div className="flex items-center justify-center min-h-dvh">
                <Spinner label={t('platform.shell.loading')} />
            </div>
        );
    }
    return <TenantProvider organizationId={activeOrganizationId}>{children}</TenantProvider>;
}
